import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import CurrentUserEnquiryContextContext from 'providers/CurrentUserEnquiryContext/CurrentUserEnquiryContext.context';
import {
  UserEnquiryContext,
  UserEnquiryContextValues,
} from 'models/UserEnquiryContext';
import { Button, Loader } from 'ncoded-component-library';
import PencilIcon from 'icons/Pencil.icon';
import TrashIcon from 'icons/Trash.icon';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import UserEnquiryForm from '../UserEnquiryForm';
import EnquiryContextTag from 'components/EnquiryContextTag';

import './EnquiryContextInstance.styles.scss';
import './EnquiryContextInstance.styles.responsive.scss';

type EnquiryContextInstanceProps = {
  className?: string;
  context: UserEnquiryContext;
  isLoading: boolean;
  isEdit: boolean;
  onEdit: () => void;
  onCancel: () => void;
};

const EnquiryContextInstance: React.FC<EnquiryContextInstanceProps> = (
  props,
) => {
  const {
    className,
    isLoading,
    isEdit,
    context: { _id, tag, description },
    onEdit,
    onCancel,
  } = props;

  const { popServerError } = useContext(PopNotificationsContext);
  const { updateIsLoadingIds, onUpdateEnquiryContext, onDeleteEnquiryContext } =
    useContext(CurrentUserEnquiryContextContext);

  const initialValues = useMemo(
    () => ({ tag, description }),
    [description, tag],
  );

  const classes = classNames('enquiry-context-instance', className);

  const onSubmit = useCallback(
    async ({ tag, description }: UserEnquiryContextValues) => {
      try {
        updateIsLoadingIds(_id);
        const { data } = await api.enquiryContext.updateEnquiryContext(
          _id,
          tag,
          description,
        );
        onUpdateEnquiryContext(data);
        onCancel();
      } catch (e) {
        popServerError(e);
      } finally {
        updateIsLoadingIds(_id, false);
      }
    },
    [_id, onCancel, onUpdateEnquiryContext, popServerError, updateIsLoadingIds],
  );

  return (
    <div className={classes}>
      {isLoading && <Loader />}

      {isEdit ? (
        <UserEnquiryForm
          initialValues={initialValues}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      ) : (
        <>
          <div className="enquiry-context-instance__heading">
            <EnquiryContextTag tag={tag} />

            <div>
              <Button variant="icon" icon={PencilIcon} onClick={onEdit} />
              <Button
                className="warning-action-button"
                variant="icon"
                icon={TrashIcon}
                onClick={() => onDeleteEnquiryContext(_id)}
              />
            </div>
          </div>

          <p>{description}</p>
        </>
      )}
    </div>
  );
};

export default EnquiryContextInstance;
